import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { FieldError, useForm, UseFormReturn } from 'react-hook-form'
import { Form } from '@/components/ui/form'
import { FormFieldData } from './fields/type'

export type FormContextType<T = any> = {
  form: UseFormReturn
  modifyTarget?: T
  setFieldGroup: (name: string, fields: FormFieldData<T>[]) => void
}

export const FormContext = createContext<FormContextType>({
  form: null,
})

export const useDetailForm = () => {
  const provider = useContext(FormContext)
  return provider
}

type Props<T> = {
  children: React.ReactNode
  data?: T
  onLoadForm?: (form: UseFormReturn) => void
}

export const FormProviderV2 = <T,>({
  children,
  data,
  onLoadForm,
}: Props<T>) => {
  const [modifyTarget, setTarget] = useState<T>()
  const fieldsGroup = useRef<Record<string, FormFieldData<T>[]>>({})

  const form = useForm({
    resolver: (values, context, options) => {
      const errors: Record<string, FieldError> = {}

      const parseKey = (
        key: string,
      ): {
        base: string
        index: number
        property: string
      } => {
        // 정규식을 사용하여 "base[index].property" 형식 파싱
        const match = key.match(/^([^\[]+)\[(\d+)\]\.(.+)$/)

        if (!match) {
          return { base: key, index: -1, property: '' }
        }

        const [_, base, indexStr, property] = match
        const index = parseInt(indexStr, 10)

        return { base, index, property }
      }

      const getValueByDotNotation = (obj: any, path: string) => {
        if (!path) {
          return undefined
        }
        const keys = path.split('.')
        let current = obj
        for (const key of keys) {
          if (current == null) {
            return undefined
          }
          current = current[key]
        }
        return current
      }

      const setError = (fieldKey: string, error: FieldError) => {
        errors[fieldKey] = error
      }

      for (const [key, fields] of Object.entries(fieldsGroup.current)) {
        if (key === '@origin') {
          for (const field of fields) {
            const fieldKey = field.key as string
            if (field.required) {
              const value = getValueByDotNotation(values, fieldKey)

              if (
                typeof value === 'undefined' ||
                value === null ||
                value === ''
              ) {
                setError(fieldKey, {
                  message: '필수항목을 입력해주세요.',
                  type: 'required',
                })
              }
            }
          }
        } else {
          for (const field of fields) {
            const fieldKey = field.key as string
            const { base, index, property } = parseKey(fieldKey)

            if (typeof index !== 'number') {
              continue
            }

            if (field.required) {
              let value
              if (property) {
                // base[index].property 형태
                if (
                  values[base] &&
                  Array.isArray(values[base]) &&
                  values[base][index]
                ) {
                  value = values[base][index][property]
                } else {
                  value = undefined
                }
              } else {
                // 혹시 base[index]만 있는 경우
                if (values[base] && Array.isArray(values[base])) {
                  value = values[base][index]
                } else {
                  value = undefined
                }
              }
              if (
                typeof value === 'undefined' ||
                value === null ||
                value === ''
              ) {
                setError(fieldKey, {
                  message: '필수항목을 입력해주세요.',
                  type: 'required',
                })
              }
            }
          }
        }
      }

      return {
        values,
        errors,
      }
    },
  })

  const setFieldGroup = useCallback(
    (name: string, fields: FormFieldData<T>[]) => {
      fieldsGroup.current[name] = fields
    },
    [],
  )

  useEffect(() => {
    if (data) {
      setTarget(data)
      form.reset(data)
    }
  }, [data, form])

  useLayoutEffect(() => {
    if (onLoadForm) {
      onLoadForm(form)
    }
  }, [onLoadForm, form])

  return (
    <FormContext.Provider
      value={{
        form,
        modifyTarget,
        setFieldGroup,
      }}
    >
      <Form {...form}>{children}</Form>
    </FormContext.Provider>
  )
}
